/**
 * Conversation memory — how a follow-up resolves against the last turn.
 *
 * The regex engine had no memory: "and at Kalbarri?" after a stock question
 * was a fresh question with no subject, and fell through to the fleet
 * overview. This module is a pure reducer over {@link ConversationMemory}:
 *
 * - {@link detectFollowUp} — is this turn a follow-up, and of which kind?
 *   - `swap`: "and at Kalbarri?", "what about L-13?", "and the drills?" —
 *     re-run the last intent, on the last question, with the new entity
 *     swapped in;
 *   - `why`: "why?" — explain the machine the last answer settled on;
 *   - `cost`: "what would that cost?" — price the job on that machine.
 * - {@link mergeEntities} — the swap rule: a new mine clears the old machine,
 *   a new machine clears the old mine, a new class clears the machine.
 * - {@link nextMemory} — what to remember after this turn.
 *
 * The memory is plain JSON (no RegExp), so the chat can persist it per thread.
 *
 * Engine layer — pure.
 */

import type { ConversationMemory } from './answer-types.ts';
import type { AnswerFocus } from './answer-kit.ts';
import { componentsFromText, extractEntities, type ClassFilter, type Entities } from './entities.ts';

/** The entities of the question as asked — what a swap merges into. */
export interface AskedEntities {
  readonly machine?: string;
  readonly mine?: Entities['mine'];
  readonly cls?: ClassFilter;
  readonly component?: string;
  readonly days?: number;
  readonly windowLabel?: string;
  readonly module?: Entities['module'];
}

/** The engine's memory shape. Every field optional; unknown keys ignored. */
export interface EngineMemory extends ConversationMemory {
  /** The question the last intent answered — a swap re-runs it. */
  readonly q?: string;
  readonly asked?: AskedEntities;
  readonly component?: string;
  readonly report?: { kind: string; mine: string | null };
  readonly ranked?: readonly string[];
  /**
   * The last turn that was a question in its own right (not "why?" / "what
   * would that cost?"). A swap that the last intent cannot take — "and the
   * loaders?" after a job cost — re-runs this one instead.
   */
  readonly root?: { readonly intent: string; readonly q: string; readonly asked: AskedEntities };
}

export type FollowUpKind = 'swap' | 'why' | 'cost';

export interface FollowUp {
  readonly kind: FollowUpKind;
}

const WHY = /^(and |but |so )?(why|how come|why is that|why so|why that|explain( that| why)?|what'?s driving (that|it)|what is driving (that|it))\s*[?.!]*$/;
const COST =
  /^(and |so |ok(ay)?,? )?(what|how much) (would|will|does|did) (that|it|this) (cost|come to|be)( to (fix|do|change|replace))?\s*[?.!]*$/;
const SWAP_LEAD = /^(and|what about|how about|same (for|at|on)|same (thing|question) (for|at|on)|now( for)?|ok(ay)?,? (and|what about)|also|then)\b/;

/** Does this turn lean on the last one? `null` = answer it on its own. */
export function detectFollowUp(
  L: string,
  E: Entities,
  memory: ConversationMemory | undefined,
  strongestScore: number,
): FollowUp | null {
  if (!memory?.intent) return null;
  const t = L.trim();
  if (WHY.test(t)) return { kind: 'why' };
  if (COST.test(t)) return { kind: 'cost' };
  const hasEntity = !!(E.machines.length || E.mines.length || E.cls || E.components.length || E.days);
  if (!hasEntity) return null;
  const words = t.split(/\s+/).filter(Boolean).length;
  // "and at Kalbarri?", "what about L-13?" — a lead word, or so short that
  // nothing but the entity is there — and no intent claims the question on
  // its own ("now show me cost per hour at Kalbarri" is a new question).
  if (strongestScore >= 3) return null;
  if (SWAP_LEAD.test(t) || words <= 4) return { kind: 'swap' };
  return null;
}

/** The swap rule. `base` is the new question's entities. */
export function mergeEntities(base: Entities, asked: AskedEntities | undefined): Entities {
  const prev = asked ?? {};
  const newMine = base.mine;
  const newMachine = base.machine;
  const newCls = base.cls;
  const machine = newMachine ?? (newMine || newCls ? undefined : prev.machine);
  const mine = newMine ?? (newMachine ? undefined : prev.mine);
  const component = base.components[0]?.word ?? prev.component;
  return {
    ...base,
    machines: machine ? [machine] : [],
    machine,
    machineFrom: machine ? (newMachine ? 'text' : 'memory') : undefined,
    mines: mine ? [mine] : [],
    mine,
    mineFrom: mine ? (newMine ? 'text' : 'memory') : undefined,
    cls: newCls ?? (newMachine ? undefined : prev.cls),
    components: base.components.length ? base.components : component ? componentsFromText(component) : [],
    days: base.days ?? prev.days,
    windowLabel: base.windowLabel ?? prev.windowLabel,
    module: base.module ?? prev.module,
  };
}

/** The entities worth remembering from a question. */
export function askedOf(E: Entities): AskedEntities {
  return {
    machine: E.machineFrom === 'text' || E.machineFrom === 'memory' ? E.machine : undefined,
    mine: E.mine,
    cls: E.cls,
    component: E.components[0]?.word,
    days: E.days,
    windowLabel: E.windowLabel,
    module: E.module,
  };
}

/** Memory after this turn. */
export function nextMemory(args: {
  intent: string | null;
  q: string;
  E: Entities;
  focus: AnswerFocus;
  memo: Record<string, unknown>;
  family?: string;
  previous?: ConversationMemory;
  followUp?: FollowUpKind;
}): EngineMemory {
  const { intent, q, E, focus, memo, family, followUp } = args;
  const previous = args.previous as EngineMemory | undefined;
  if (!intent) {
    // A miss keeps the thread's context so the next turn can still follow up.
    return { ...(previous ?? {}), missed: q } as EngineMemory;
  }
  const clean = (o: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));
  const asked = clean(askedOf(E) as Record<string, unknown>) as AskedEntities;
  const root =
    followUp === 'why' || followUp === 'cost'
      ? (previous?.root ?? (previous?.intent && previous.q ? { intent: previous.intent, q: previous.q, asked: previous.asked ?? {} } : undefined))
      : { intent, q, asked };
  return clean({
    root,
    intent,
    q,
    topic: family,
    machine: focus.machine ?? E.machine,
    mine: focus.mine ?? E.mine,
    component: (memo.component as string | undefined) ?? focus.component ?? E.components[0]?.word,
    alert: focus.alert,
    asked,
    report: memo.report ?? previous?.report,
    ...memo,
  }) as EngineMemory;
}

/** Re-read a remembered question's entities (used by a swap). */
export const entitiesOf = (q: string): Entities => extractEntities(q);
